/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Grid, xcss } from '@atlaskit/primitives';
import { R400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import type { SearchPageNode } from '@atlassian/search-client';

import { type ApplicationModes } from '../../constants/application-modes';
import { type PrimaryProductKey } from '../../constants/products';
import type { ResultClickedCallback } from '../../constants/types';
import SearchResultContextMenu from '../context-menu';
import { SearchMetadata } from '../search-metadata';
import { SearchResultTitle } from '../search-result-title';
import { SummarizeInChatButton } from '../summarize-in-chat-button';

import { messages } from './messages';

const SUMMARIZE_IN_CHAT_CONTAINER_WIDTH_BREAKPOINT = process.env.NODE_ENV === 'test' ? 0 : 700;

const searchResultContainerStyles = xcss({
	position: 'relative',
});

const iconAreaStyles = xcss({
	gridArea: 'icon',
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'center',
	width: token('space.600'),
});

const newIconStyles = xcss({
	marginBlockStart: 'space.075',
});

const headerRightAreaStylesInVisible = xcss({
	opacity: 0,
});

const headerRightAreaStylesVisible = xcss({
	opacity: 1,
});

const bodyAreaStyles = xcss({
	gridArea: 'body',
	paddingTop: 'space.050',
	overflowWrap: 'break-word',
});

const footerAreaStyles = xcss({
	gridArea: 'footer',
});

const lockIconStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	marginInlineEnd: 'space.075',
	paddingTop: 'space.025',
});

const headerRightContainerStyles = xcss({
	gridArea: 'header-right',
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'flex-end',
});

const summarizeInChatStyles = xcss({
	marginInlineEnd: 'space.050',
});

export type SearchResultProps = {
	id?: string;
	product?: string;
	productSelected?: boolean;
	primaryProduct?: PrimaryProductKey;
	description: ReactNode;
	footer?: ReactNode;
	icon: ReactNode;
	headerMetadataParts?: any[]; // TODO: CHANGE THIS TO A MORE SPECIFIC TYPE
	headerMetadataSocialParts?: any[]; // TODO: CHANGE THIS TO A MORE SPECIFIC TYPE
	titleMetadata?: ReactNode;
	title: ReactNode;
	url?: string;
	isThirdParty?: boolean;
	showAISummary?: boolean;
	position?: number;
	/** The ARIA role for the search result */
	role?: string;
	/**
	 * @private
	 * @deprecated Passing in the full partial is ignored by the component. Pass in the required props directly instead.
	 * TODO: https://product-fabric.atlassian.net/browse/QS-7698
	 **/
	partial?: SearchPageNode;
	cloudId?: string;
	appMode?: ApplicationModes;
	/** Called when the result is clicked */
	onResultClicked?: ResultClickedCallback;

	/** Called when the result context menu is opened */
	onResultContextMenu?: VoidFunction;

	/** Called when the copy link button is clicked */
	onCopyLinkButtonClicked?: VoidFunction;

	/** Called when the AI summary button is clicked */
	onAISummaryButtonClicked?: VoidFunction;

	/** Called when the summarize in chat button is clicked */
	onSummarizeInChatButtonClicked?: VoidFunction;
	isLoadingSkeleton?: boolean;
	linkedResults?: SearchPageNode;
	isLinkedResult?: boolean;
	/** Used to render the lock icon in result context menu */
	isPrivate?: boolean;
	/** Whether the user is eligible for fanout linked results */
	isFanoutTreatmentUser?: boolean;
};

/**
 * This component is intended to be as generic as possible.
 * It should provide generic "slots" for users of this component to add
 * their content in.
 */
export const SearchResult = ({
	id,
	product,
	productSelected,
	primaryProduct,
	description,
	footer,
	icon,
	url,
	showAISummary = false,
	role = 'listitem',
	cloudId, // required for AI Summaries
	title,
	isThirdParty,
	appMode,
	headerMetadataParts,
	headerMetadataSocialParts,
	titleMetadata,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onAISummaryButtonClicked,
	onSummarizeInChatButtonClicked,
	isLoadingSkeleton = false,
	isLinkedResult = false,
	isPrivate,
}: SearchResultProps) => {
	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
	const [containerWidth, setContainerWidth] = useState(0);
	const isContextMenuVisible = !isLinkedResult && (isHovered || isFocused || isContextMenuOpen);
	const isSummarizeInChatVisible =
		!isLinkedResult &&
		(isHovered || isFocused) &&
		containerWidth >= SUMMARIZE_IN_CHAT_CONTAINER_WIDTH_BREAKPOINT;

	const containerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const updateContainerWidth = () => {
			if (containerRef.current) {
				setContainerWidth(containerRef.current.offsetWidth);
			}
		};

		const resizeObserver = new ResizeObserver(updateContainerWidth);

		if (containerRef.current) {
			resizeObserver.observe(containerRef.current);
			// Set initial width
			updateContainerWidth();
		}

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	const aiSummaryEnabled = Boolean(showAISummary && id && cloudId && primaryProduct);

	const sainFrontendExperimentName = useMemo(
		() =>
			FeatureGates.getLayer('kd_sain_layer', {
				fireLayerExposure: false,
			}).get('frontendExperimentName', ''),
		[],
	);
	const serveSummarizeInChatCustomerExp = useMemo(
		() =>
			sainFrontendExperimentName === 'kd_summarize_in_chat' &&
			FeatureGates.getExperimentValue<string>(sainFrontendExperimentName, 'cohort', 'control') ===
				'test',
		[sainFrontendExperimentName],
	);

	const sainHelloFrontendExperimentName = useMemo(
		() =>
			FeatureGates.getLayer('kd_sain_hello_layer', {
				fireLayerExposure: false,
			}).get('frontendExperimentName', ''),
		[],
	);
	const serveSummarizeInChatHelloExp = useMemo(
		() =>
			sainHelloFrontendExperimentName === 'kd_summarize_in_chat_hello' &&
			FeatureGates.getExperimentValue<string>(
				sainHelloFrontendExperimentName,
				'cohort',
				'control',
			) === 'test',
		[sainHelloFrontendExperimentName],
	);

	const serveSummarizeInChatExp = serveSummarizeInChatCustomerExp || serveSummarizeInChatHelloExp;

	return (
		<Box
			role={role}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocusCapture={() => setIsFocused(true)}
			onBlurCapture={() => setIsFocused(false)}
			testId={isLoadingSkeleton ? 'search-page-result-skeleton' : 'search-page-result'}
			ref={containerRef}
			xcss={searchResultContainerStyles}
		>
			<Grid
				columnGap="space.150"
				rowGap="space.050"
				templateAreas={[
					'icon heading header-right',
					'icon subheading-left header-right',
					'icon body body',
					'icon footer footer',
				]}
				templateColumns="auto 1fr auto"
			>
				<Box xcss={iconAreaStyles}>
					<Box xcss={newIconStyles}>{icon}</Box>
				</Box>
				<SearchResultTitle
					id={id}
					title={title}
					url={url}
					isThirdParty={isThirdParty}
					appMode={appMode}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultContextMenu}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					isHovered={isHovered}
					titleMetadata={
						!serveSummarizeInChatExp || !isSummarizeInChatVisible ? titleMetadata : undefined
					}
				/>
				<SearchMetadata
					searchResultId={id}
					product={product}
					productSelected={productSelected}
					parts={headerMetadataParts}
					socialParts={headerMetadataSocialParts}
					containerRef={containerRef}
				/>
				<Box xcss={headerRightContainerStyles}>
					{isPrivate && (!serveSummarizeInChatExp || !isSummarizeInChatVisible) && (
						<Box xcss={lockIconStyles}>
							<Tooltip content={formatMessage(messages.messageResultLockIconTooltip)}>
								<LockLockedIcon
									label={formatMessage(messages.privateChannelLabel)}
									color={token('color.icon.accent.red', R400)}
									size="small"
								/>
							</Tooltip>
						</Box>
					)}

					{!isLoadingSkeleton && serveSummarizeInChatExp && isSummarizeInChatVisible && (
						<Box xcss={[summarizeInChatStyles]} testId="search-result-summarize-in-chat">
							<SummarizeInChatButton url={url} onClick={onSummarizeInChatButtonClicked} />
						</Box>
					)}

					{!isLoadingSkeleton && ( // Context Menu should not be rendered when rendering as a loading skeleton
						<Box
							xcss={
								isContextMenuVisible ? headerRightAreaStylesVisible : headerRightAreaStylesInVisible
							}
							testId="search-result-context-menu"
						>
							<SearchResultContextMenu
								url={url}
								isContextMenuOpen={isContextMenuOpen}
								containerWidth={containerWidth}
								setIsContextMenuOpen={setIsContextMenuOpen}
								setIsFocused={setIsFocused}
								setIsHovered={setIsHovered}
								id={id || ''}
								cloudId={cloudId}
								aiSummaryEnabled={aiSummaryEnabled}
								onAISummaryButtonClicked={onAISummaryButtonClicked}
								onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
							/>
						</Box>
					)}
				</Box>
				<Box xcss={bodyAreaStyles}>{description}</Box>
				<Box xcss={footerAreaStyles}>{footer}</Box>
			</Grid>
		</Box>
	);
};
